import { ApiClient } from "../api-client";
import { ApiClientError, handleApiErrorWithSchema, UnauthenticatedError } from "../errors";
import lmsInfoSchemaValidators from "../lms-info/schema-validators";
import { TypeFromSchemaValidator } from "../schema-validator";
import schemaValidators from "./schema-validators";

export default class PermissionClient extends ApiClient<typeof schemaValidators> {
	constructor(config: ApiClient["config"]) {
		super(config, schemaValidators);
	}

	doesUserHaveTestResultPermission = async (
		quizInfo: TypeFromSchemaValidator<(typeof lmsInfoSchemaValidators)["assessmentInfo"]>,
	): Promise<boolean> => {
		try {
			const resp = await super.fetch(
				new URL(
					`/api/verity/test-results/${quizInfo.lms}/${quizInfo.courseId ?? ""}/${quizInfo.quizId}?verify=permissions`,
					`${this.config.verityOrigin}`,
				),
				{ credentials: "include" },
			);
			if (resp.status === 403) return false; // if user is not auth, then error is expected
			const data: unknown = await resp.json();
			if (this.schemaValidators.verityResultPermission.validate(data)) {
				return data.verified;
			}
			return false;
		} catch (e) {
			if (e instanceof Error) {
				throw new ApiClientError(e);
			}
			return false;
		}
	};

	portalPermissions = async (lmsCourseId?: string | null) => {
		try {
			const resp = await super.fetch(
				new URL(`/api/verity/portal/permissions?lmsCourseId=${lmsCourseId}`, `${this.config.verityOrigin}`),
				{ credentials: "include" },
			);
			if (resp.ok) {
				const data: unknown = await resp.json();
				if (this.schemaValidators.portalPermission.validate(data)) {
					return data;
				}
			}
		} catch (e) {
			if (e instanceof Error) {
				throw new ApiClientError(e);
			}
		}
		return [];
	};

	// TODO: get permission from access-control token
	fromHostname = async (
		info: Pick<TypeFromSchemaValidator<(typeof lmsInfoSchemaValidators)["assessmentInfo"]>, "hostname">,
	): Promise<TypeFromSchemaValidator<(typeof schemaValidators)["assessmentPermissions"]>> => {
		try {
			const res = await super.fetch(
				new URL(`/api/verity/permissions/hostname/${info.hostname}`, `${this.config.verityOrigin}`),
			);
			if (res.ok) {
				const data: unknown = await res.json();
				if (this.schemaValidators.assessmentPermissions.validate(data)) {
					return data;
				}
			} else if (res.status === 401) {
				throw new UnauthenticatedError();
			}
		} catch (e) {
			if (e instanceof Error) {
				throw new ApiClientError(e);
			}
		}
		return {
			assessment: [],
		};
	};

	doesUserHaveInstituteProfilePermissions = async (): Promise<boolean> => {
		try {
			const res = await super.fetch(
				new URL(`/api/verity/permissions/institute-profiles`, `${this.config.verityOrigin}`),
			);
			if (res.ok) {
				return true;
			} else if (res.status === 401) {
				throw new UnauthenticatedError();
			}
			return false;
		} catch (e) {
			if (e instanceof Error) {
				throw new ApiClientError(e);
			}
		}
		return false;
	};

	getUserPrivileges = () =>
		handleApiErrorWithSchema(
			super.fetch(new URL(`/api/access-control/user/self/privilege`, `${this.config.verityOrigin}`)),
			this.schemaValidators.scopedPrivileges,
		);
}
